import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Checkbox, Col, Drawer, Form, Input, InputNumber, Row, Select, Space, Spin, Tabs, Tree } from "antd";
import { serverFetch } from "../../../core/server";
import { allLawLeafKeys, buildLawTreeData, checkedKeysFromLaw, lawFromCheckedKeys, normalizeLaw } from "./user-law";
import "./spr-sotr.css";

const normalizeIdBlank = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const createEmptySotr = () => ({
  idSotr: 0,
  name: "",
  fullName: "",
  dolj: "",
  userLevel: 0,
  depno: 0,
  idBlank: null,
  shk: "",
  crmUser: "",
  inn: "",
  idPred: 0,
  bbOnly: false,
  crm: true,
  isDel: false,
  minSale: 0,
  maxSale: 0,
  minSaleIzd: 0,
  maxSaleIzd: 0,
  minSaleAks: 0,
  maxSaleAks: 0,
  minSaleJob: 0,
  maxSaleJob: 0,
  law: normalizeLaw(""),
  passwordEmpty: true
});

const SotrNumber = ({ label, name }) => (
  <Form.Item label={label} name={name}>
    <InputNumber />
  </Form.Item>
);

const SprSotr = ({ open, idSotr, onClose, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [checkedLawKeys, setCheckedLawKeys] = useState([]);
  const [lawExpandedKeys, setLawExpandedKeys] = useState([]);
  const lawTreeData = useMemo(() => buildLawTreeData(), []);
  const lawAllLeafKeys = useMemo(() => allLawLeafKeys(), []);
  const [blanks, setBlanks] = useState([]);
  const [blanksLoading, setBlanksLoading] = useState(false);
  const blankOptions = useMemo(
    () =>
      blanks.map((row) => ({
        value: row.idBlank,
        label: String(row.name ?? "").trim() || `Бланк ${row.idBlank}`
      })),
    [blanks]
  );
  const isNew = !idSotr;

  const resetLawUi = useCallback(() => {
    setLawExpandedKeys([]);
  }, []);

  const loadSotr = useCallback(async () => {
    if (!open) {
      return;
    }

    resetLawUi();
    setError("");
    if (isNew) {
      const empty = createEmptySotr();
      form.setFieldsValue({ ...empty, idBlank: null, passwordEmpty: true });
      setCheckedLawKeys(checkedKeysFromLaw(empty.law));
      return;
    }

    setLoading(true);
    try {
      const item = await serverFetch(`app/database/sotrs/${idSotr}`, { method: "GET" });
      const next = {
        ...createEmptySotr(),
        ...item,
        law: normalizeLaw(item?.law),
        idBlank: normalizeIdBlank(item?.idBlank),
        passwordEmpty: !!(item?.passwordEmpty ?? item?.PasswordEmpty)
      };
      form.setFieldsValue(next);
      setCheckedLawKeys(checkedKeysFromLaw(next.law));
    } catch (e) {
      setError(e.message || "Не удалось загрузить пользователя");
    } finally {
      setLoading(false);
    }
  }, [form, idSotr, isNew, open, resetLawUi]);

  useEffect(() => {
    loadSotr();
  }, [loadSotr]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setBlanksLoading(true);
    serverFetch("sprs/blanks", { method: "GET" })
      .then((data) => {
        if (!active) return;
        setBlanks(Array.isArray(data) ? data : []);
      })
      .catch(() => active && setBlanks([]))
      .finally(() => active && setBlanksLoading(false));
    return () => {
      active = false;
    };
  }, [open]);

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const values = await form.validateFields();
      const formValues = Object.fromEntries(
        Object.entries(values).filter(([key]) => !["pass", "Pass", "password"].includes(key))
      );
      const body = {
        ...createEmptySotr(),
        ...formValues,
        idSotr: isNew ? 0 : Number(values?.idSotr ?? idSotr) || 0,
        idBlank: normalizeIdBlank(values?.idBlank),
        passwordEmpty: !!values.passwordEmpty,
        law: lawFromCheckedKeys(checkedLawKeys)
      };
      const saved = await serverFetch("app/database/sotrs", {
        method: isNew ? "POST" : "PUT",
        bodyData: body
      });
      onSaved?.(saved);
    } catch (e) {
      setError(e.message || "Не удалось сохранить пользователя");
    } finally {
      setSaving(false);
    }
  };

  const onCheckLaw = (keys) => {
    setCheckedLawKeys(Array.isArray(keys) ? keys : keys?.checked || []);
  };

  const checkAllLaw = () => setCheckedLawKeys([...lawAllLeafKeys]);

  const uncheckAllLaw = () => setCheckedLawKeys([]);

  const onExpandLaw = (expandedKeys) => {
    setLawExpandedKeys(expandedKeys);
  };

  const tabs = [
    {
      key: "main",
      label: "Основное",
      children: (
        <div className="spr-sotr-drawer__tab-scroll">
          <Form.Item name="idSotr" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item label="Имя пользователя" name="name" rules={[{ required: true, message: "Введите имя пользователя" }]}>
            <Input autoFocus />
          </Form.Item>
          <Form.Item name="passwordEmpty" valuePropName="checked">
            <Checkbox>Пустой пароль</Checkbox>
          </Form.Item>
          <Form.Item label="Полное имя" name="fullName">
            <Input />
          </Form.Item>
          <Form.Item label="Должность" name="dolj">
            <Input />
          </Form.Item>
          <Row gutter={[12, 8]}>
            <Col xs={24} sm={12} lg={8}>
              <SotrNumber label="Уровень доступа" name="userLevel" />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SotrNumber label="Отдел по умолчанию" name="depno" />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <Form.Item label="Бланк" name="idBlank">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Не выбран"
                  loading={blanksLoading}
                  options={blankOptions}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[12, 8]}>
            <Col xs={24} md={8}>
              <Form.Item label="Штрихкод" name="shk">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="CRM пользователь" name="crmUser">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="ИНН" name="inn">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[12, 8]}>
            <Col xs={24} sm={12} lg={8}>
              <SotrNumber label="Контрагент" name="idPred" />
            </Col>
          </Row>
          <div className="spr-sotr-drawer__checks">
            <Form.Item name="bbOnly" valuePropName="checked">
              <Checkbox>Безбумажные операции только по умолчанию</Checkbox>
            </Form.Item>
            <Form.Item name="crm" valuePropName="checked">
              <Checkbox>Доступен для CRM</Checkbox>
            </Form.Item>
            <Form.Item name="isDel" valuePropName="checked">
              <Checkbox>Удален</Checkbox>
            </Form.Item>
          </div>
        </div>
      )
    },
    {
      key: "discounts",
      label: "Скидки",
      children: (
        <div className="spr-sotr-drawer__tab-scroll">
          <Row gutter={[12, 8]}>
            <Col xs={24} sm={12}>
              <SotrNumber label="Заказ мин." name="minSale" />
            </Col>
            <Col xs={24} sm={12}>
              <SotrNumber label="Заказ макс." name="maxSale" />
            </Col>
          </Row>
          <Row gutter={[12, 8]}>
            <Col xs={24} sm={12}>
              <SotrNumber label="Изделия мин." name="minSaleIzd" />
            </Col>
            <Col xs={24} sm={12}>
              <SotrNumber label="Изделия макс." name="maxSaleIzd" />
            </Col>
          </Row>
          <Row gutter={[12, 8]}>
            <Col xs={24} sm={12}>
              <SotrNumber label="Аксессуары мин." name="minSaleAks" />
            </Col>
            <Col xs={24} sm={12}>
              <SotrNumber label="Аксессуары макс." name="maxSaleAks" />
            </Col>
          </Row>
          <Row gutter={[12, 8]}>
            <Col xs={24} sm={12}>
              <SotrNumber label="Работы мин." name="minSaleJob" />
            </Col>
            <Col xs={24} sm={12}>
              <SotrNumber label="Работы макс." name="maxSaleJob" />
            </Col>
          </Row>
        </div>
      )
    },
    {
      key: "law",
      label: "Права",
      children: (
        <div className="spr-sotr-law-pane">
          <div className="spr-sotr-law">
            <div className="spr-sotr-law__toolbar">
              <Space size={8} wrap>
                <Button size="small" type="default" onClick={checkAllLaw} disabled={loading || saving}>
                  Отметить все
                </Button>
                <Button size="small" type="default" onClick={uncheckAllLaw} disabled={loading || saving}>
                  Убрать все
                </Button>
              </Space>
            </div>
            <div className="spr-sotr-law__tree">
              <Tree
                checkable
                blockNode
                treeData={lawTreeData}
                checkedKeys={checkedLawKeys}
                expandedKeys={lawExpandedKeys}
                onExpand={onExpandLaw}
                onCheck={onCheckLaw}
              />
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <Drawer
      placement="right"
      title="Пользователь"
      width={760}
      open={open}
      onClose={onClose}
      destroyOnClose
      maskClosable={!saving}
      closable={{ disabled: saving, placement: "end" }}
      className="spr-sotr-drawer"
      footer={
        <div className="spr-sotr-drawer__footer">
          <Button type="primary" loading={saving} onClick={save}>
            OK
          </Button>
          <Button onClick={onClose} disabled={saving}>
            Отмена
          </Button>
        </div>
      }
    >
      {error ? <Alert type="error" message={error} showIcon style={{ marginBottom: 12, flexShrink: 0 }} /> : null}
      <div className="spr-sotr-drawer__main">
        <Spin spinning={loading}>
          <Form form={form} layout="vertical" disabled={loading || saving} className="spr-sotr-drawer__form" colon={false}>
            <Tabs items={tabs} />
          </Form>
        </Spin>
      </div>
    </Drawer>
  );
};

export default SprSotr;
